import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

export function formatCPF(cpf: string): string {
  if (!cpf) return '';
  const digits = cpf.replace(/\D/g, '').slice(0, 11);
  if (digits.length !== 11) return cpf;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9, 11)}`;
}

export function formatRG(rg: string): string {
  if (!rg) return '';
  const digits = rg.replace(/\D/g, '').slice(0, 9);
  if (digits.length !== 9) return rg;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}-${digits.slice(8, 9)}`;
}

export function formatCNPJ(cnpj: string): string {
  if (!cnpj) return '-';
  const digits = cnpj.replace(/\D/g, '').slice(0, 14);
  if (digits.length !== 14) return cnpj;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12, 14)}`;
}

export function formatPhone(phone: string): string {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11) {
    // (XX) XXXXX-XXXX
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
  }
  if (digits.length === 10) {
    // (XX) XXXX-XXXX
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6, 10)}`;
  }
  return phone;
}

export function formatDays(days: string, short: boolean = false): string {
  if (!days) return '';
  const dayNames = short
    ? ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab']
    : ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
  return days
    .split('')
    .map((v, i) => (v === '1' ? dayNames[i] : null))
    .filter(Boolean)
    .join(', ');
}

export function parseBrazilianCurrency(val: string) {
  if (!val) return 0;
  return parseFloat(val.replace(/\./g, '').replace(',', '.'));
}

export function formatDateBR(
  date: string | Date,
  includeTime: boolean = false,
): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  const dateStr = d.toLocaleDateString('pt-BR');
  if (includeTime) {
    const timeStr = d.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    return `${dateStr} ${timeStr}`;
  }
  return dateStr;
}

export function formatProfessionalCouncil(
  councilName?: string,
  stateCode?: string,
  configuredRegistrationType?: string,
): string {
  const configuredLabel = (configuredRegistrationType || '').trim();
  if (configuredLabel) return configuredLabel;
  const baseCouncilName = (councilName || '')
    .trim()
    .replace(/(?:\s+|[-/])[A-Z]{2}$/i, '')
    .trim();
  const normalizedStateCode = (stateCode || '').trim().toUpperCase();
  const regionalCouncilName = (() => {
    switch (baseCouncilName.toUpperCase()) {
      case 'CFM':
      case 'CREMESP':
        return 'CRM';
      case 'COFEN':
        return 'COREN';
      default:
        return baseCouncilName;
    }
  })();

  if (!regionalCouncilName) return normalizedStateCode;
  if (!normalizedStateCode) return regionalCouncilName;
  return `${regionalCouncilName}-${normalizedStateCode}`;
}

export function formatBRL(val: number | string): string {
  if (val === null || val === undefined || val === '') return '';
  const num = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(num)) return '';
  return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function fixUTCDate(
  dateString: string | null,
  useUTC = true,
  addTime = false,
) {
  if (!dateString) return '-';

  const date = new Date(dateString);

  const day = String(useUTC ? date.getUTCDate() : date.getDate()).padStart(
    2,
    '0',
  );
  const month = String(
    useUTC ? date.getUTCMonth() + 1 : date.getMonth() + 1,
  ).padStart(2, '0');
  const year = useUTC ? date.getUTCFullYear() : date.getFullYear();
  const hours = String(useUTC ? date.getUTCHours() : date.getHours()).padStart(
    2,
    '0',
  );
  const minutes = String(
    useUTC ? date.getUTCMinutes() : date.getMinutes(),
  ).padStart(2, '0');

  const seconds = String(
    useUTC ? date.getUTCSeconds() : date.getSeconds(),
  ).padStart(2, '0');

  let formattedDate = addTime
    ? `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`
    : `${day}/${month}/${year}`;

  return formattedDate;
}

export function parseDateAsUTC(
  dateStr: string | null | undefined,
): Date | null {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
  );
}

// Schedule timestamps carry the operational clock used by the calendar.
// Mirror CalendarMainPage's parseDateAsUTC before formatting: converting the
// original timestamp directly to the browser timezone shifts the displayed hour.
const scheduleDisplayDate = (value?: string | null): Date | null => {
  const date = parseDateAsUTC(value);
  return date && !Number.isNaN(date.getTime()) ? date : null;
};

export function formatScheduleTime(value?: string | null): string {
  const date = scheduleDisplayDate(value);
  return date
    ? new Intl.DateTimeFormat('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      }).format(date)
    : '--:--';
}

export function formatScheduleDateTime(value?: string | null): string {
  const date = scheduleDisplayDate(value);
  return date
    ? new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      }).format(date)
    : '—';
}

export function getScheduleDateParts(value?: string | null) {
  const date = scheduleDisplayDate(value);
  if (!date) return { day: '--', month: '---', weekday: '-' };
  return {
    day: new Intl.DateTimeFormat('pt-BR', { day: '2-digit' }).format(date),
    month: new Intl.DateTimeFormat('pt-BR', { month: 'short' })
      .format(date)
      .replace('.', '')
      .toUpperCase(),
    weekday: new Intl.DateTimeFormat('pt-BR', { weekday: 'long' }).format(date),
  };
}

export function formatPostalCode(postalCode: string): string {
  if (!postalCode) return '';
  const digits = postalCode.replace(/\D/g, '').slice(0, 8);
  if (digits.length !== 8) return postalCode;
  return `${digits.slice(0, 5)}-${digits.slice(5, 8)}`;
}

export function getShiftHours(startTime, endTime, withMinutes = true) {
  if (!startTime || !endTime) return 0;

  const start = dayjs(startTime, 'HH:mm');
  let end = dayjs(endTime, 'HH:mm');

  if (end.isBefore(start)) {
    end = end.add(1, 'day');
  }

  return end.diff(start, 'hour', withMinutes);
}

export function applyPixMask(value: string, pixType: string): string {
  if (!value) return '';

  const cleanValue = value.replace(/\D/g, '');

  switch (pixType.toLowerCase()) {
    case 'cpf':
      return formatCPF(cleanValue);
    case 'cnpj':
      return formatCNPJ(cleanValue);
    case 'telefone':
    case 'phone':
      return formatPhone(cleanValue);
    case 'email':
    case 'e-mail':
      return value;
    case 'chave aleatória':
    case 'random':
    default:
      return value;
  }
}

export function removePixMask(value: string, pixType: string): string {
  if (!value) return '';

  switch (pixType.toLowerCase()) {
    case 'cpf':
    case 'cnpj':
    case 'telefone':
    case 'phone':
      return value.replace(/\D/g, '');
    case 'email':
    case 'e-mail':
    case 'chave aleatória':
    case 'random':
    default:
      return value;
  }
}

export function getPixMask(pixType: string): string {
  switch (pixType.toLowerCase()) {
    case 'cpf':
      return '999.999.999-99';
    case 'cnpj':
      return '99.999.999/9999-99';
    case 'telefone':
    case 'phone':
      return '(99) 99999-9999';
    default:
      return '';
  }
}
